import { useState, useEffect, useCallback } from 'react';
import { BlogListView } from './BlogListView';
import { BlogPostView } from './BlogPostView';

interface BlogRouterProps {
  onNavigateHome: () => void;
}

function getSlugFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/blog\/([^/]+)$/);
  return match ? decodeURIComponent(match[1]) : null;
}

function isBlogListPath(pathname: string): boolean {
  return pathname === '/blog' || pathname === '/blog/';
}

/**
 * Handles client-side routing for the blog (/blog and /blog/:slug).
 * Uses window.history.pushState + popstate to keep the URL in sync.
 */
export const BlogRouter: React.FC<BlogRouterProps> = ({ onNavigateHome }) => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Sync to browser back/forward navigation
  useEffect(() => {
    const onPopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigateToPost = useCallback((slug: string) => {
    const path = `/blog/${slug}`;
    window.history.pushState({}, '', path);
    setCurrentPath(path);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const navigateToList = useCallback(() => {
    window.history.pushState({}, '', '/blog');
    setCurrentPath('/blog');
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleNavigateHome = useCallback(() => {
    window.history.pushState({}, '', '/');
    onNavigateHome();
  }, [onNavigateHome]);

  const slug = getSlugFromPath(currentPath);

  if (slug) {
    return (
      <BlogPostView
        slug={slug}
        onNavigateList={navigateToList}
        onNavigateHome={handleNavigateHome}
      />
    );
  }

  if (isBlogListPath(currentPath)) {
    return (
      <BlogListView
        onNavigatePost={navigateToPost}
        onNavigateHome={handleNavigateHome}
      />
    );
  }

  // Fallback: go to list
  return (
    <BlogListView
      onNavigatePost={navigateToPost}
      onNavigateHome={handleNavigateHome}
    />
  );
};
